import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { previewAdapter } from "./previewAdapter";
import { resolveApiBaseUrl } from "./apiBase";

const isPreview = process.env.REACT_APP_UI_PREVIEW === "true";
type logsFilters = {
  ts_from: string | null;
  ts_to: string | null;
  provider: string | null;
  status: number[];
  url_like: string | null;
};
const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  ...(isPreview ? { adapter: previewAdapter } : {}),
});

// Логин

export async function loginUser({
  username,
  password,
}: {
  username: string;
  password: string;
}) {
  return handleRequest(
    api.post("/auth/login/", { username, password }, { withCredentials: true }),
  );
}
// Двухфакторная аутентификация (TOTP)
//
// Второй шаг входа опирается на короткоживущую cookie `twofa_token` (5 минут),
// которую бэкенд ставит в ответ на /auth/login. Поэтому в теле нужен только код —
// пользователя определяет cookie, а не переданный идентификатор.
export type TwoFactorStatus = {
  enabled: boolean;
  /** Секрет создан через /setup, но ещё не подтверждён кодом. */
  pending_setup: boolean;
};

export type TwoFactorSetup = {
  secret: string;
  otpauth_url: string;
  issuer: string;
};

/** Второй шаг входа. Вызывается, только если /auth/login вернул twofa_required. */
export async function loginVerify2fa(code: string) {
  return handleRequest(
    api.post("/auth/login/2fa", { code }, { withCredentials: true }),
  );
}

export const getTwoFactorStatus = async () => {
  return handleRequest(api.get("/auth/2fa/status", { withCredentials: true }));
};

/** Создаёт секрет и отдаёт otpauth-ссылку. Пока не подтверждён кодом — не активен. */
export const setupTwoFactor = async () => {
  return handleRequest(
    api.post("/auth/2fa/setup", {}, { withCredentials: true }),
  );
};

export const enableTwoFactor = async (code: string) => {
  return handleRequest(
    api.post("/auth/2fa/enable", { code }, { withCredentials: true }),
  );
};

/** Требует действующий код: перехваченная сессия не должна снимать защиту. */
export const disableTwoFactor = async (code: string) => {
  return handleRequest(
    api.post("/auth/2fa/disable", { code }, { withCredentials: true }),
  );
};

/**
 * Снимает второй фактор с пользователя, потерявшего доступ к приложению.
 * Резервных кодов в системе нет, поэтому это единственный путь восстановления.
 */
export const resetUserTwoFactor = async (userId: number) => {
  return handleRequest(
    api.post(`/auth/users/${userId}/2fa/reset`, {}, { withCredentials: true }),
  );
};

// Управление пользователями панели. Все ручки доступны только роли admin.

export type PanelRole = "admin" | "merchant" | "support" | "ref";

export type PanelUser = {
  id: number;
  username: string;
  role: PanelRole;
  twofa_enabled: boolean;
  /** Второй фактор заблокирован перебором — снимается сбросом 2FA. */
  twofa_locked: boolean;
  created_at: string | null;
  password_changed_at: string | null;
  /** Заполнен, если на пользователе висит мерчант: роль тогда менять нельзя. */
  merchant_id: number | null;
};

/**
 * Ответ на создание пользователя и на сброс пароля.
 * `password` приходит только когда бэкенд сгенерировал его сам, и только один
 * раз — показать его нужно сразу, повторно узнать будет негде.
 */
export type PasswordIssued = {
  id: number;
  username: string;
  generated_password: boolean;
  password: string | null;
  sessions_revoked?: boolean;
};

export const getPanelUsers = async (params?: {
  role?: PanelRole | "";
  search?: string;
}) => {
  const query: Record<string, string> = {};
  if (params?.role) query.role = params.role;
  if (params?.search?.trim()) query.search = params.search.trim();

  return handleRequest(
    api.get("/auth/users", { params: query, withCredentials: true }),
  );
};

export const getPanelUser = async (userId: number) => {
  return handleRequest(
    api.get(`/auth/users/${userId}`, { withCredentials: true }),
  );
};

export const getPanelRoles = async () => {
  return handleRequest(api.get("/auth/roles", { withCredentials: true }));
};

/** Пароль можно не передавать — тогда бэкенд сгенерирует его сам. */
export const createPanelUser = async (payload: {
  username: string;
  role: PanelRole;
  password?: string;
}) => {
  const body: Record<string, unknown> = {
    username: payload.username.trim(),
    role: payload.role,
  };
  if (payload.password) body.password = payload.password;

  return handleRequest(api.post("/auth/users", body, { withCredentials: true }));
};

/** Меняет имя и роль. Оба поля необязательные, пароль сюда не входит. */
export const updatePanelUser = async (
  userId: number,
  payload: { username?: string; role?: PanelRole },
) => {
  const body: Record<string, unknown> = {};
  if (payload.username !== undefined) body.username = payload.username.trim();
  if (payload.role !== undefined) body.role = payload.role;

  return handleRequest(
    api.patch(`/auth/users/${userId}`, body, { withCredentials: true }),
  );
};

/**
 * Выдаёт новый пароль вместо утерянного и обрывает все сессии пользователя.
 * Без явного пароля бэкенд сгенерирует стойкий сам — так безопаснее.
 */
export const resetPanelUserPassword = async (
  userId: number,
  password?: string,
) => {
  return handleRequest(
    api.post(
      `/auth/users/${userId}/password/reset`,
      password ? { password } : {},
      { withCredentials: true },
    ),
  );
};

export const deletePanelUser = async (userId: number) => {
  return handleRequest(
    api.delete(`/auth/users/${userId}`, { withCredentials: true }),
  );
};

//Статистика транзакций
export const getStats = async (
  date: string,
  merchid: number[],
  amountFrom: number,
  amountTo: number,
  curr: string,
  methods: string[],
) => {
  const rawPayload = {
    date,
    merchid,
    amountFrom: amountFrom ? Number(amountFrom) : undefined,
    amountTo: amountTo ? Number(amountTo) : undefined,
    currency: curr ? curr : "rub",
    methods: methods.length > 0 ? methods : undefined,
  };

  const cleanedPayload = Object.fromEntries(
    Object.entries(rawPayload).filter(([_, value]) => value !== undefined),
  );

  return handleRequest(
    api.post("/transactions/payin-stats/", cleanedPayload, {
      withCredentials: true,
    }),
  );
};
// Проверка на авторизацию

export async function checkAuth() {
  return handleRequest(api.get("/me/", { withCredentials: true }));
}

//общая функция для обработки
const handleRequest = async (request: any) => {
  try {
    const response = await request;
    return response;
  } catch (err) {
    console.error("Ошибка при выполнении запроса:", err);
    throw err;
  }
};

// Мерчанты
export const fetchMerchant = async () => {
  return handleRequest(api.get("/merchants"));
};
export const createMerchant = async ({
  merchant_name,
  username,
  fiat,
  callback_url_payin,
  callback_url_payout,
}: {
  merchant_name: string;
  username: string;
  fiat: boolean;
  callback_url_payin: string;
  callback_url_payout: string;
}) => {
  return handleRequest(
    api.post(
      "/merchants/create",
      {
        merchant_name,
        username,
        fiat,
        callback_url_payin,
        callback_url_payout,
      },
      { withCredentials: true },
    ),
  );
};
export const fetchMethods = async () => {
  return handleRequest(api.get("/transactions/available_methods/"));
};
export const getRates = async () => {
  return handleRequest(api.get("/rates", { withCredentials: true }));
};
/**
 * Ставка мерчанта.
 *
 * use_payment_form и payment_ttl_seconds подчиняются правилу «не прислали — не
 * трогаем»: отсутствие поля оставляет настройку как была, а null сбрасывает её
 * к поведению по умолчанию. Это разные вещи, поэтому undefined здесь означает
 * «не отправлять ключ вовсе», и форма обязана передавать его именно так.
 */
export const changeMerchRates = async ({
  merchant_id,
  payment_method,
  operation_type,
  currency,
  rate,
  minsum,
  maxsum,
  use_payment_form,
  payment_ttl_seconds,
}: {
  merchant_id: number;
  payment_method: string;
  operation_type: string;
  currency: string;
  rate: number;
  minsum: number;
  maxsum: number;
  use_payment_form?: boolean | null;
  payment_ttl_seconds?: number | null;
}) => {
  const body: Record<string, unknown> = {
    merchant_id,
    payment_method,
    operation_type,
    currency,
    rate,
    minsum,
    maxsum,
  };

  if (use_payment_form !== undefined) body.use_payment_form = use_payment_form;
  if (payment_ttl_seconds !== undefined) {
    body.payment_ttl_seconds = payment_ttl_seconds;
  }

  return handleRequest(api.put("/rates/", body, { withCredentials: true }));
};
export const deleteRate = async ({
  merchant_id,
  payment_method,
  operation_type,
  currency,
  minsum,
  maxsum,
}: {
  merchant_id: number;
  payment_method: string;
  operation_type: string;
  currency: string;
  minsum: number;
  maxsum: number;
}) => {
  return handleRequest(
    api.delete("/rates/delete_rate", {
      data: {
        merchant_id,
        payment_method,
        operation_type,
        currency,
        minsum,
        maxsum,
      },
      withCredentials: true,
    }),
  );
};

// Провайдеры
export const fetchProviders = async () => {
  return handleRequest(api.get("/providers", { withCredentials: true }));
};

export const fetchProvRates = async () => {
  return handleRequest(api.get("/provider_rates", { withCredentials: true }));
};
export const putProvRates = async (
  provider_id: number,
  payment_method: string,
  operation_type: string,
  currency: string,
  rate: number,
  minsum: number,
  maxsum: number,
) => {
  return handleRequest(
    api.put(
      "/provider_rates",
      {
        provider_id,
        payment_method,
        operation_type,
        currency,
        rate,
        minsum,
        maxsum,
      },
      { withCredentials: true },
    ),
  );
};
/**
 * Правит существующий терминал провайдера по его id.
 *
 * PUT /provider_rates для этого не годится: он ищет запись по составному ключу
 * (провайдер, метод, операция, валюта, диапазон сумм), поэтому изменение лимитов
 * создало бы вторую ставку вместо правки первой.
 */
export const updateProviderTerminal = async (
  terminalId: number,
  patch: {
    rate?: number;
    minsum?: number;
    maxsum?: number;
    payment_method?: string;
    operation_type?: string;
    currency?: string;
  },
) => {
  return handleRequest(
    api.patch(`/provider_rates/terminal/${terminalId}`, patch, {
      withCredentials: true,
    }),
  );
};

export const deleteProvRate = async (
  provider_id: number,
  payment_method: string,
  operation_type: string,
  currency: string,
  rate: number,
  minsum: number,
  maxsum: number,
) => {
  return handleRequest(
    api.delete("/provider_rates/delete_rate", {
      data: {
        provider_id,
        payment_method,
        operation_type,
        currency,
        rate,
        minsum,
        maxsum,
      },
      withCredentials: true,
    }),
  );
};

// Каскады провайдеров
//
// Ключ каскада — это ровно тот же составной ключ, что и у MerchantPaymentRate:
// merchant_id + payment_method + operation_type + currency + minsum + maxsum.
// Бэкенд ищет rate по точному совпадению всех шести полей, поэтому minsum/maxsum
// обязаны прийти теми же числами, что вернул GET /rates.
export type CascadeRateKey = {
  merchant_id: number;
  payment_method: string;
  operation_type: string;
  currency: string;
  minsum: number;
  maxsum: number;
};

export type ProviderTerminal = {
  id: number;
  provider_terminal_id: number;
  provider_id: number;
  provider_name: string;
  payment_method: string;
  operation_type: string;
  currency: string;
  rate: number;
  minsum: number | null;
  maxsum: number | null;
};

export type CascadeItem = ProviderTerminal & {
  cascade_item_id: number;
  priority: number;
  enabled: boolean;
};

export type CascadeResponse = {
  merchant_rate: {
    merchant_id: number;
    payment_method: string;
    operation_type: string;
    currency: string;
    rate: number;
    minsum: number;
    maxsum: number;
  };
  eligible_provider_terminals: ProviderTerminal[];
  cascade: CascadeItem[];
};

export const getCascadeByRate = async (key: CascadeRateKey) => {
  return handleRequest(
    api.get("/provider_cascades/rate", {
      params: key,
      withCredentials: true,
    }),
  );
};

export const getCascadeByAmount = async ({
  merchant_id,
  payment_method,
  operation_type,
  amount,
  currency,
}: {
  merchant_id: number;
  payment_method: string;
  operation_type: string;
  amount: number;
  currency?: string;
}) => {
  return handleRequest(
    api.get("/provider_cascades/rate/by_amount", {
      params: { merchant_id, payment_method, operation_type, amount, currency },
      withCredentials: true,
    }),
  );
};

/** Полностью заменяет каскад. priority можно не слать — возьмётся порядок массива. */
export const setCascade = async (
  key: CascadeRateKey,
  items: { provider_terminal_id: number; priority?: number; enabled: boolean }[],
) => {
  return handleRequest(
    api.put(
      "/provider_cascades/rate",
      { ...key, items },
      { withCredentials: true },
    ),
  );
};

/**
 * Полностью снимает каскад с тарифа.
 *
 * Тело у DELETE обязательно, поэтому ключ уходит через `data` — axios иначе
 * отправил бы запрос без него, и бэкенд ответил бы 422.
 */
export const deleteCascade = async (key: CascadeRateKey) => {
  return handleRequest(
    api.delete("/provider_cascades/rate", {
      data: key,
      withCredentials: true,
    }),
  );
};

/** Добавляет один шаг, не переписывая каскад целиком. */
export const addCascadeItem = async (
  key: CascadeRateKey,
  item: { provider_terminal_id: number; priority?: number; enabled?: boolean },
) => {
  return handleRequest(
    api.post(
      "/provider_cascades/rate/item",
      { ...key, ...item },
      { withCredentials: true },
    ),
  );
};

/** Убирает один шаг. Оставшиеся бэкенд перенумеровывает подряд. */
export const removeCascadeItem = async (
  key: CascadeRateKey,
  provider_terminal_id: number,
) => {
  return handleRequest(
    api.delete("/provider_cascades/rate/item", {
      data: { ...key, provider_terminal_id },
      withCredentials: true,
    }),
  );
};

/** Не удаляет терминал из каскада, только выводит его из очереди запросов. */
export const toggleCascadeItem = async (
  key: CascadeRateKey,
  provider_terminal_id: number,
  enabled: boolean,
) => {
  return handleRequest(
    api.patch(
      "/provider_cascades/rate/item",
      { ...key, provider_terminal_id, enabled },
      { withCredentials: true },
    ),
  );
};

/** Массив обязан содержать все терминалы каскада: без пропусков и дублей. */
export const reorderCascade = async (
  key: CascadeRateKey,
  provider_terminal_ids: number[],
) => {
  return handleRequest(
    api.post(
      "/provider_cascades/rate/reorder",
      { ...key, provider_terminal_ids },
      { withCredentials: true },
    ),
  );
};

// Баланс
export const fetchBalance = async () => {
  return handleRequest(
    api.get("/balance", {
      withCredentials: true,
    }),
  );
};
export const fetchRefBalance = async () => {
  return handleRequest(
    api.get("/referral_balance", {
      withCredentials: true,
    }),
  );
};
export async function changeBalanceMerch({
  merchant_name,
  transaction_type,
  amount,
  course,
  currency,
}: {
  merchant_name: string;
  transaction_type: string;
  amount: number;
  course: number;
  currency: string;
}) {
  return handleRequest(
    api.post(
      "transactions/change_balance/",
      { merchant_name, transaction_type, amount, course, currency },
      { withCredentials: true },
    ),
  );
}
// Транзакции
/**
 * Перечитывает одну транзакцию.
 *
 * Отдельной ручки «дай транзакцию по id» нет, поэтому идём через тот же поиск,
 * что и строка поиска в шапке. Он ищет по подстроке сразу в трёх полях, так что
 * по короткому id может вернуться несколько записей — точное совпадение
 * выбираем здесь, иначе в карточку попала бы чужая операция.
 */
export const refreshTransaction = async (id: string) => {
  const res = await handleRequest(
    api.post(
      "/transactions/get_stats/",
      { filters: { search_query: id } },
      { withCredentials: true },
    ),
  );

  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.find((row: any) => row?.id === id) ?? null;
};

export const getTrans = async (filters: any) => {
  return handleRequest(
    api.post(
      "/transactions/get_stats/",
      { filters },
      {
        withCredentials: true,
      },
    ),
  );
};
// Логи
export const getInLogs = async (page: number, filters: logsFilters) => {
  const body: Record<string, any> = { page };

  if (
    filters.ts_from ||
    filters.ts_to ||
    filters.provider ||
    filters.url_like ||
    filters.status.length > 0
  ) {
    body.filters = {};
    if (filters.ts_from) body.filters.ts_from = filters.ts_from;
    if (filters.ts_to) body.filters.ts_to = filters.ts_to;
    if (filters.provider) body.filters.provider = filters.provider;
    if (filters.url_like) body.filters.url_like = filters.url_like;
    if (filters.status.length > 0) body.filters.status = filters.status;
  }
  return handleRequest(
    api.post("/panel/logs/inbound/list", body, { withCredentials: true }),
  );
};
export const getOutLogs = async (page: number, filters: logsFilters) => {
  const body: Record<string, any> = { page };

  if (
    filters.ts_from ||
    filters.ts_to ||
    filters.provider ||
    filters.url_like ||
    filters.status.length > 0
  ) {
    body.filters = {};
    if (filters.ts_from) body.filters.ts_from = filters.ts_from;
    if (filters.ts_to) body.filters.ts_to = filters.ts_to;
    if (filters.provider) body.filters.provider = filters.provider;
    if (filters.url_like) body.filters.url_like = filters.url_like;
    if (filters.status.length > 0) body.filters.status = filters.status;
  }

  return handleRequest(
    api.post("/panel/logs/outbound/list", body, { withCredentials: true }),
  );
};
export const getReqIdLogs = async (id: string, filters: logsFilters) => {
  const body: Record<string, any> = { request_id: id };

  if (
    filters.ts_from ||
    filters.ts_to ||
    filters.provider ||
    filters.url_like ||
    filters.status.length > 0
  ) {
    body.filters = {};
    if (filters.ts_from) body.filters.ts_from = filters.ts_from;
    if (filters.ts_to) body.filters.ts_to = filters.ts_to;
    if (filters.provider) body.filters.provider = filters.provider;
    if (filters.url_like) body.filters.url_like = filters.url_like;
    if (filters.status.length > 0) body.filters.status = filters.status;
  }

  return handleRequest(
    api.post("/panel/logs/by_request_id", body, { withCredentials: true }),
  );
};
export const getTransIdLogs = async (id: string, filters: logsFilters) => {
  const body: Record<string, any> = { transaction_id: id };

  if (
    filters.ts_from ||
    filters.ts_to ||
    filters.provider ||
    filters.url_like ||
    filters.status.length > 0
  ) {
    body.filters = {};
    if (filters.ts_from) body.filters.ts_from = filters.ts_from;
    if (filters.ts_to) body.filters.ts_to = filters.ts_to;
    if (filters.provider) body.filters.provider = filters.provider;
    if (filters.url_like) body.filters.url_like = filters.url_like;
    if (filters.status.length > 0) body.filters.status = filters.status;
  }

  return handleRequest(
    api.post("/panel/logs/by_transaction_id", body, { withCredentials: true }),
  );
};
export const getLatest = async ({ page }: { page: number }) => {
  return handleRequest(
    api.post("/transactions/get_list/", { page }, { withCredentials: true }),
  );
};
export const getLatestTest = async ({
  page,
  filters,
}: {
  page: number;
  filters: any;
}) => {
  const payload =
    Object.keys(filters).length > 0 ? { page, filters } : { page };
  return handleRequest(
    api.post("/transactions/get_list_test/", payload, {
      withCredentials: true,
    }),
  );
};
export const transStatusChange = async (
  newStatus: string,
  transactionId: string,
) => {
  return handleRequest(
    api.put(
      `/transactions/update_status/${transactionId}/`,
      { status: newStatus },
      { withCredentials: true },
    ),
  );
};
export const transAmountChange = async (
  newAmount: number,
  transactionId: string,
) => {
  return handleRequest(
    api.put(
      `/transactions/update_amount/${transactionId}/`,
      { sum: newAmount },
      { withCredentials: true },
    ),
  );
};

export type ExportFormat = "csv" | "xlsx";

/**
 * Бэкенд разбирает даты через strptime("%d.%m.%Y").
 *
 * DocumentForm передаёт уже готовую строку dd.mm.yyyy, но тип поля объявлен как
 * Dayjs, и другие вызовы шлют объект. Приводим оба случая к одному виду, иначе
 * Dayjs ушёл бы в JSON как ISO и бэкенд ответил бы 400.
 */
const toExportDay = (value?: Dayjs | string | null): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") {
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : undefined;
  }
  return value.isValid() ? value.format("DD.MM.YYYY") : undefined;
};

export const downloadFile = async ({
  status,
  createdFrom,
  createdTo,
  type,
  merchid,
  forMerch,
  updatedFrom,
  updatedTo,
  format = "csv",
}: {
  status: string[];
  createdFrom?: Dayjs | string | null;
  createdTo?: Dayjs | string | null;
  updatedFrom?: Dayjs | string | null;
  updatedTo?: Dayjs | string | null;
  type: string[];
  merchid: number[];
  forMerch: boolean;
  format?: ExportFormat;
}) => {
  const body: Record<string, any> = {
    status,
    type,
    merchid,
    forMerch,
    format,
  };

  const created_from = toExportDay(createdFrom);
  const created_to = toExportDay(createdTo);
  const updated_from = toExportDay(updatedFrom);
  const updated_to = toExportDay(updatedTo);

  if (created_from) body.createdFrom = created_from;
  if (created_to) body.createdTo = created_to;
  if (updated_from) body.updatedFrom = updated_from;
  if (updated_to) body.updatedTo = updated_to;

  return handleRequest(
    api.post("/transactions/export/csv/", body, {
      withCredentials: true,
      // xlsx — бинарный, при разборе как текст файл портится.
      responseType: "blob",
    }),
  );
};
export const changePassword = async ({
  old_password,
  new_password,
}: {
  old_password: string;
  new_password: string;
}) => {
  return handleRequest(
    api.post(
      `/auth/change_password`,
      { old_password, new_password },
      { withCredentials: true },
    ),
  );
};
export const sendCB = async (transId: string) => {
  return handleRequest(
    api.post(
      `/transactions/send_callback/`,
      { id: transId },
      { withCredentials: true },
    ),
  );
};

// ---------------------------------------------------------------------------
// Методы оплаты
//
// Реестр методов живёт в базе и пополняется из админки: список больше не зашит
// в бэкенде. Поэтому `payment_method` везде — обычная строка, а незнакомый код
// показывается как есть, а не заменяется заглушкой.
// ---------------------------------------------------------------------------

export type PhoneRule = {
  country_code: string;
  national_len: number;
  local_prefixes: string[];
};

export type PaymentMethodItem = {
  code: string;
  currency: string | null;
  payment_form: boolean;
  card_form: boolean;
  page_code: string | null;
  phone: PhoneRule | null;
  bank_aliases: Record<string, string>;
  updated_at: string | null;
  /** «код» — строки в базе нет, метод работает по умолчанию. */
  source: "база" | "код";
};

export type PaymentMethodsResponse = {
  items: PaymentMethodItem[];
  currencies: string[];
  note?: string;
};

/** Тело POST и PUT: PUT замещает настройки целиком, а не дополняет их. */
export type PaymentMethodPayload = {
  currency?: string | null;
  payment_form?: boolean;
  card_form?: boolean;
  page_code?: string | null;
  phone?: PhoneRule | null;
  bank_aliases?: Record<string, string>;
};

export const getPaymentMethods = async () => {
  return handleRequest(api.get("/payment_methods/", { withCredentials: true }));
};

export const createPaymentMethod = async (
  code: string,
  payload: PaymentMethodPayload,
) => {
  return handleRequest(
    api.post("/payment_methods/", { code, ...payload }, { withCredentials: true }),
  );
};

/**
 * Замещает настройки метода целиком: непереданное поле сбрасывается в значение
 * по умолчанию. Форму нужно заполнять текущими значениями, иначе правка одного
 * поля сотрёт остальные.
 */
export const updatePaymentMethod = async (
  code: string,
  payload: PaymentMethodPayload,
) => {
  return handleRequest(
    api.put(`/payment_methods/${code}`, payload, { withCredentials: true }),
  );
};

/** Нужно редко: POST и PUT перечитывают реестр сами. */
export const reloadPaymentMethods = async () => {
  return handleRequest(
    api.post("/payment_methods/reload", {}, { withCredentials: true }),
  );
};

// ---------------------------------------------------------------------------
// Наборы ключей провайдеров
//
// Значения ключей не отдаются никогда — ни при создании, ни при чтении. Экран
// показывает только имена полей; утраченный набор заводится заново.
// ---------------------------------------------------------------------------

export type CredentialScope = {
  payment_method?: string | null;
  operation_type?: string | null;
  currency?: string | null;
  bank_name?: string | null;
  minsum?: number | null;
  maxsum?: number | null;
};

export type ProviderCredential = {
  id: number;
  provider_id: number;
  provider: string;
  label: string;
  scope: CredentialScope;
  /** null, если набор прочитать не удалось. */
  fields: string[] | null;
  /** false — набор зашифрован другим ключом или повреждён. */
  readable: boolean;
  /**
   * Бэкенд возвращает и принимает это поле, но админка им не управляет:
   * наборы различаются областью действия, а не ручным порядком. Разрешать
   * ничью должен бэкенд, отклоняя набор с той же областью при создании.
   */
  priority: number;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
};

/**
 * Описание одного поля ключа. Имена угадать нельзя: у fastpay переменная
 * окружения зовётся FASTPAY_SECRET, а поле набора — SECRET_KEY, и ошибка в
 * имени тихая — набор выглядит настроенным, а платежи идут по окружению.
 * Поэтому имена даёт бэкенд, а форма спрашивает только значения.
 */
export type CredentialFieldSpec = {
  /** Уйдёт ключом в payload. Пользователю не показывается. */
  name: string;
  /** Подпись поля — так его называет сам провайдер. */
  title: string;
  /** Куда уходит значение: заголовок, поле тела, подпись. */
  hint: string | null;
  required: boolean;
  /** true — прятать ввод. У идентификаторов false: они и так уходят открыто. */
  secret: boolean;
  /** Что действует, пока поле не заполнено. Показываем подсказкой в поле. */
  default: string | null;
  /** Переменная окружения, откуда ключ брался раньше. null — её нет. */
  env: string | null;
};

/** Счёт провайдера: группа полей плюс признаки, по которым он выбирается. */
export type CredentialAccountSpec = {
  code: string;
  title: string;
  note: string | null;
  /** Чем счёт выбирается. Этим предзаполняем область набора. */
  scope: {
    payment_methods: string[];
    operation_type: string | null;
    amount_from: number | null;
    amount_to: number | null;
  };
  fields: CredentialFieldSpec[];
  /** Имена без суффикса — принимаются у набора, ограниченного методом. */
  generic_fields: string[];
};

export type ProviderCredentialSchema = {
  provider_id: number;
  provider: string;
  title: string;
  /** false — набор для этого провайдера заведётся и ничего не сделает. */
  reads_sets: boolean;
  /** Пояснение для человека: как провайдер выбирает счёт. */
  note: string | null;
  /** Поля вне счёта — сейчас только BASE_URL. Идут в тот же payload. */
  common_fields: CredentialFieldSpec[];
  accounts: CredentialAccountSpec[];
  /** Поля без запасной переменной окружения: работают только через набор. */
  without_env: string[];
};

/**
 * Какие поля нужны провайдеру. Без provider_id — схемы всех провайдеров.
 *
 * Пустой accounts при reads_sets: true означает, что поля провайдера ещё не
 * описаны, — тогда остаётся прежний ввод имён вручную.
 */
export const getProviderCredentialFields = async (providerId?: number) => {
  return handleRequest(
    api.get("/provider_credentials/fields", {
      params: providerId ? { provider_id: providerId } : {},
      withCredentials: true,
    }),
  );
};

export const getProviderCredentials = async (providerId?: number) => {
  return handleRequest(
    api.get("/provider_credentials/", {
      params: providerId ? { provider_id: providerId } : {},
      withCredentials: true,
    }),
  );
};

export const createProviderCredential = async (payload: {
  provider_id: number;
  label: string;
  payload: Record<string, string>;
  scope?: CredentialScope;
  enabled?: boolean;
}) => {
  return handleRequest(
    api.post("/provider_credentials/", payload, { withCredentials: true }),
  );
};

/** Точечная правка. payload — исключение: он заменяет набор ключей целиком. */
export const updateProviderCredential = async (
  credentialId: number,
  patch: {
    label?: string;
    payload?: Record<string, string>;
    scope?: CredentialScope;
    enabled?: boolean;
  },
) => {
  return handleRequest(
    api.patch(`/provider_credentials/${credentialId}`, patch, {
      withCredentials: true,
    }),
  );
};

export const deleteProviderCredential = async (credentialId: number) => {
  return handleRequest(
    api.delete(`/provider_credentials/${credentialId}`, {
      withCredentials: true,
    }),
  );
};

// ---------------------------------------------------------------------------
// Валюты
//
// Устроены как методы оплаты: реестр живёт в базе и пополняется из админки.
// Сама по себе валюта ничего не включает — она лишь становится доступной для
// выбора. Заказы по ней пойдут только после того, как в ней заведут метод
// оплаты, а по методу — тариф мерчанта.
// ---------------------------------------------------------------------------

export type CurrencyItem = {
  code: string;
  title: string | null;
  /**
   * Знак валюты — то, что стоит рядом с суммой: ₽, ₼, Bs.
   *
   * Пустым не бывает: когда знак не задан, бэкенд отдаёт код заглавными.
   * Поэтому «знак равен коду в верхнем регистре» и значит «не задан».
   */
  symbol: string;
  /** Знаков после запятой по ISO 4217. */
  minor_units: number;
  /** Имя валюты в ответах мерчанту, если оно отличается от кода. */
  api_alias: string | null;
  updated_at: string | null;
  /** «код» — строки в базе нет, валюта работает по умолчанию. */
  source: "база" | "код";
  /** Методы, заказы по которым идут в этой валюте. Пустой — валюта ни на что не влияет. */
  methods: string[];
};

export type CurrencyPayload = {
  title?: string | null;
  /** Не длиннее 8 символов. null или пусто — бэкенд подставит код заглавными. */
  symbol?: string | null;
  minor_units?: number;
  api_alias?: string | null;
};

export const getCurrencies = async () => {
  return handleRequest(api.get("/currencies/", { withCredentials: true }));
};

export const createCurrency = async (
  code: string,
  payload: CurrencyPayload,
) => {
  return handleRequest(
    api.post("/currencies/", { code, ...payload }, { withCredentials: true }),
  );
};

/**
 * Меняет название, знак, разрядность и алиас. Код не меняется: он лежит в типе
 * PostgreSQL и в уже сохранённых заявках — валюту с другим кодом заводят заново.
 */
export const updateCurrency = async (
  code: string,
  payload: CurrencyPayload,
) => {
  return handleRequest(
    api.put(`/currencies/${code}`, payload, { withCredentials: true }),
  );
};

/** Перечитывает валюты и методы. Нужно редко: правка перечитывает сама. */
export const reloadCurrencies = async () => {
  return handleRequest(
    api.post("/currencies/reload", {}, { withCredentials: true }),
  );
};
